import { getLocalTimeZone, today } from "@internationalized/date";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarHeaderCell,
  composeRenderProps,
  type DateValue,
  Heading,
  Calendar as RACCalendar,
  CalendarGridHeader as RACCalendarGridHeader,
  type CalendarProps as RACCalendarProps,
  useLocale,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Button } from "@/components/ui/button";
import { cn, focusRing } from "@/components/ui/utils";

const cellStyles = tv({
  extend: focusRing,
  base: [
    "relative flex size-9 items-center justify-center whitespace-nowrap rounded-lg border border-transparent p-0 text-sm font-normal text-foreground outline-offset-2 duration-150 [transition-property:color,background-color,border-radius,box-shadow]",
    "data-unavailable:pointer-events-none data-unavailable:line-through data-unavailable:opacity-30",
    "data-hovered:bg-accent data-hovered:text-foreground",
  ],
  variants: {
    isSelected: {
      true: "bg-primary text-primary-foreground",
    },
    isDisabled: {
      true: "pointer-events-none opacity-30",
    },
    isToday: {
      true: "after:pointer-events-none after:absolute after:bottom-1 after:start-1/2 after:z-10 after:size-[3px] after:-translate-x-1/2 after:rounded-full after:bg-primary",
    },
  },
});

export function Calendar<T extends DateValue>(props: RACCalendarProps<T>) {
  const now = today(getLocalTimeZone());

  return (
    <RACCalendar
      {...props}
      className={composeRenderProps(props.className, (className) =>
        cn("w-fit", className),
      )}
    >
      <CalendarHeader />
      <CalendarGrid>
        <CalendarGridHeader />
        <CalendarGridBody>
          {(date) => (
            <CalendarCell
              date={date}
              className={(renderProps) =>
                cellStyles({ ...renderProps, isToday: date.compare(now) === 0 })
              }
            />
          )}
        </CalendarGridBody>
      </CalendarGrid>
    </RACCalendar>
  );
}

export function CalendarHeader() {
  const { direction } = useLocale();

  return (
    <header className="flex items-center gap-1 pb-4 px-1 w-full">
      <Button variant="ghost" size="icon-sm" slot="previous">
        {direction === "rtl" ? (
          <ChevronRight aria-hidden />
        ) : (
          <ChevronLeft aria-hidden />
        )}
      </Button>
      <Heading className="grow text-center text-sm font-medium" />
      <Button variant="ghost" size="icon-sm" slot="next">
        {direction === "rtl" ? (
          <ChevronLeft aria-hidden />
        ) : (
          <ChevronRight aria-hidden />
        )}
      </Button>
    </header>
  );
}

export function CalendarGridHeader() {
  return (
    <RACCalendarGridHeader>
      {(day) => (
        <CalendarHeaderCell className="text-xs text-gray-500 font-semibold">
          {day}
        </CalendarHeaderCell>
      )}
    </RACCalendarGridHeader>
  );
}
